import { useEffect, useRef, useState } from "react";
import { useRouterState } from "@tanstack/react-router";
import { toast } from "sonner";
import { Flag, Loader2, Mic, Square } from "lucide-react";

import { reportTextIssue } from "@/braining/issue-report";
import { captureScreen } from "@/braining/screen-capture";
import { useSpeech } from "@/braining/use-speech";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";

/**
 * Выделил текст — пожаловался.
 *
 * Не всё, что не так, живёт в ассистенте: подпись колонки, формулировка
 * уточнения, число в своде. Раньше это доходило до разработчика, только если
 * кто-то не поленился написать. Теперь у любого выделения на экране есть
 * кнопка: нажал, объяснил в одну строку — и задача заведена с адресом экрана,
 * цитатой и тем, что рядом.
 *
 * Не срабатывает в полях ввода и в ассистенте: там выделяют свой текст, а у
 * ответов ассистента есть своя жалоба с полным контекстом разговора.
 */

interface Pick {
  text: string;
  near?: string;
  x: number;
  y: number;
  /** где выделение на экране — по нему снимок обводит место жалобы */
  rect: DOMRect;
}

export function ReportSelection() {
  const path = useRouterState({ select: (s) => s.location.pathname });
  const [pick, setPick] = useState<Pick | null>(null);
  const [open, setOpen] = useState(false);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const box = useRef<HTMLDivElement>(null);

  /*
   * Жалобу тоже диктуют.
   *
   * Диктовка уже есть у ассистента и у заведения блока — а объяснить, что не
   * так, человеку обычно труднее всего, и печатать это он будет неохотно.
   * Сказанное дописывается к набранному, а не заменяет его: половину мысли
   * часто уже напечатали.
   */
  const speech = useSpeech((said) => setNote((prev) => (prev ? `${prev} ${said}` : said)));

  useEffect(() => {
    function onUp() {
      // Пока открыт диалог, выделение внутри него — не новая жалоба.
      if (open) return;
      const sel = window.getSelection();
      const text = sel?.toString().trim() ?? "";
      if (!sel || sel.rangeCount === 0 || text.length < 3) {
        setPick(null);
        return;
      }
      const node = sel.anchorNode;
      const el = node instanceof Element ? node : node?.parentElement;
      if (!el || el.closest("input, textarea, [contenteditable], [data-no-report]")) {
        setPick(null);
        return;
      }
      const rect = sel.getRangeAt(0).getBoundingClientRect();
      setPick({
        text: text.slice(0, 2000),
        near: nearestLabel(el),
        x: rect.right,
        y: rect.top,
        rect,
      });
    }
    function onDown(e: MouseEvent) {
      if (box.current?.contains(e.target as Node)) return;
      if (!open) setPick(null);
    }
    document.addEventListener("mouseup", onUp);
    document.addEventListener("mousedown", onDown);
    return () => {
      document.removeEventListener("mouseup", onUp);
      document.removeEventListener("mousedown", onDown);
    };
  }, [open]);

  async function send() {
    if (!pick) return;
    // Микрофон гаснет до снимка: работающая запись оставляет в браузере
    // индикатор, и он попал бы в кадр.
    speech.stop();
    setBusy(true);
    try {
      const wizard = document.querySelector<HTMLElement>("[data-step]");
      const estimateId = /\/estimates\/([0-9a-f-]{36})/.exec(path)?.[1];
      // Снимок — пока диалог открыт: экран должен быть таким, каким его видел
      // человек. Сам диалог в снимок не попадает: он вне `main`.
      //
      // Место выделения берём заново, если оно на месте: между выделением и
      // нажатием страница могла прокрутиться. Но только если выделен тот же
      // текст — клик по кнопке «Отправить» схлопывает выделение в точку
      // внутри самой кнопки, и рамка уезжала в угол снимка.
      const live = window.getSelection();
      const same = live && live.rangeCount > 0 && live.toString().trim() === pick.text;
      const capture = await captureScreen(
        same ? live.getRangeAt(0).getBoundingClientRect() : pick.rect,
      );
      const res = await reportTextIssue({
        data: {
          capture,
          path,
          screen: wizard?.dataset.step,
          estimateId,
          selection: pick.text,
          near: pick.near,
          note: note.trim() || undefined,
        },
      });
      if (res.ok) toast.success(`Спасибо, жалоба №${res.id} записана — разберёмся`);
      else
        toast.warning(`Жалоба №${res.id} записана, но разработчику не ушла`, {
          description: res.error,
        });
    } catch {
      toast.error("Жалоба не записана — сервер не ответил");
    } finally {
      setBusy(false);
      setOpen(false);
      setPick(null);
      setNote("");
      window.getSelection()?.removeAllRanges();
    }
  }

  if (!pick) return null;

  // Кнопка — у правого верхнего угла выделения, диалог — там же, но не за
  // краем экрана.
  const left = Math.min(pick.x + 8, window.innerWidth - 360);
  const top = Math.max(8, pick.y - 40);

  return (
    <div ref={box} data-no-report className="fixed z-50" style={{ left, top }}>
      {open ? (
        <div className="w-[340px] space-y-2 rounded-lg border bg-card p-3 shadow-xl">
          <p className="max-h-20 overflow-hidden text-xs text-muted-foreground">«{pick.text}»</p>
          <Textarea
            autoFocus
            rows={2}
            value={speech.listening && speech.interim ? `${note} ${speech.interim}`.trim() : note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="Что здесь не так? Можно надиктовать."
            className="text-sm"
          />
          {speech.error && <p className="text-xs text-warning-ink">{speech.error}</p>}
          <div className="flex items-center justify-end gap-1.5">
            {speech.supported && (
              <Button
                size="sm"
                variant="ghost"
                className="mr-auto"
                onClick={speech.toggle}
                title={speech.listening ? "Остановить запись" : "Надиктовать"}
                aria-label={speech.listening ? "Остановить запись" : "Надиктовать"}
              >
                {speech.listening ? (
                  <Square className="size-4 text-destructive" />
                ) : (
                  <Mic className="size-4" />
                )}
              </Button>
            )}
            <Button
              size="sm"
              variant="ghost"
              onClick={() => {
                speech.stop();
                setOpen(false);
                setPick(null);
              }}
            >
              Отмена
            </Button>
            <Button size="sm" disabled={busy} onClick={() => void send()}>
              {busy ? <Loader2 className="size-4 animate-spin" /> : <Flag className="size-4" />}
              Отправить
            </Button>
          </div>
        </div>
      ) : (
        <button
          type="button"
          onMouseDown={(e) => e.preventDefault()}
          onClick={() => setOpen(true)}
          className="flex items-center gap-1.5 rounded-full border bg-card px-3 py-1.5 text-xs shadow-lg transition-colors hover:bg-muted"
        >
          <Flag className="size-3.5" />
          Сообщить об ошибке
        </button>
      )}
    </div>
  );
}

/** Ближайшая подпись: заголовок карточки, метка поля, шапка колонки. */
function nearestLabel(el: Element): string | undefined {
  const own = el.closest("th, label, h1, h2, h3, h4, button, [role=heading]");
  if (own && own.textContent?.trim()) return own.textContent.trim().slice(0, 200);
  const card = el.closest("section, article, [data-slot=card], .rounded-xl, .rounded-lg");
  const head = card?.querySelector("h1, h2, h3, h4, label, [role=heading]");
  return head?.textContent?.trim().slice(0, 200) || undefined;
}
